import { TAU } from "../core/math";

// Simple vector (canvas-path) upgrade icons — no emoji, no image assets. Each
// icon is drawn centered at (cx, cy) within a box of `size`, tinted `color`.
export function drawIcon(ctx: CanvasRenderingContext2D, icon: string, cx: number, cy: number, size: number, color: string): void {
  const s = size / 2; // half-extent
  ctx.save();
  ctx.translate(cx, cy);
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = Math.max(1.4, size * 0.11);
  ctx.lineJoin = "round";
  ctx.lineCap = "round";

  switch (icon) {
    case "bolt": // damage — lightning
      ctx.beginPath();
      ctx.moveTo(s * 0.25, -s);
      ctx.lineTo(-s * 0.5, s * 0.1);
      ctx.lineTo(-s * 0.05, s * 0.1);
      ctx.lineTo(-s * 0.25, s);
      ctx.lineTo(s * 0.5, -s * 0.1);
      ctx.lineTo(s * 0.05, -s * 0.1);
      ctx.closePath();
      ctx.fill();
      break;

    case "chevrons": // attack speed — >>
      for (const dx of [-s * 0.4, s * 0.2]) {
        ctx.beginPath();
        ctx.moveTo(dx - s * 0.15, -s * 0.6);
        ctx.lineTo(dx + s * 0.35, 0);
        ctx.lineTo(dx - s * 0.15, s * 0.6);
        ctx.stroke();
      }
      break;

    case "radiate": // range — radiating arcs
      for (let i = 1; i <= 3; i++) {
        ctx.globalAlpha = i / 3;
        ctx.beginPath();
        ctx.arc(0, s * 0.4, s * 0.45 * i, -Math.PI * 0.8, -Math.PI * 0.2);
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
      ctx.beginPath();
      ctx.arc(0, s * 0.45, size * 0.07, 0, TAU);
      ctx.fill();
      break;

    case "target": // crit — crosshair
      ctx.beginPath();
      ctx.arc(0, 0, s * 0.8, 0, TAU);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(0, 0, s * 0.3, 0, TAU);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(-s, 0);
      ctx.lineTo(-s * 0.5, 0);
      ctx.moveTo(s * 0.5, 0);
      ctx.lineTo(s, 0);
      ctx.moveTo(0, -s);
      ctx.lineTo(0, -s * 0.5);
      ctx.moveTo(0, s * 0.5);
      ctx.lineTo(0, s);
      ctx.stroke();
      break;

    case "arrow": // projectile speed — arrow
      ctx.beginPath();
      ctx.moveTo(-s * 0.75, 0);
      ctx.lineTo(s * 0.55, 0);
      ctx.moveTo(s * 0.1, -s * 0.4);
      ctx.lineTo(s * 0.7, 0);
      ctx.lineTo(s * 0.1, s * 0.4);
      ctx.stroke();
      break;

    case "chain": // chain / pierce — linked rings
      ctx.beginPath();
      ctx.arc(-s * 0.35, 0, s * 0.42, 0, TAU);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(s * 0.35, 0, s * 0.42, 0, TAU);
      ctx.stroke();
      break;

    case "split": // extra projectile — fork
      ctx.beginPath();
      ctx.moveTo(-s * 0.7, 0);
      ctx.lineTo(-s * 0.05, 0);
      ctx.stroke();
      for (const a of [-0.55, 0, 0.55]) {
        ctx.beginPath();
        ctx.moveTo(-s * 0.05, 0);
        ctx.lineTo(s * 0.7, a * s);
        ctx.stroke();
      }
      break;

    case "boots": // move speed — stacked chevrons
      for (const dy of [s * 0.35, -s * 0.3]) {
        ctx.beginPath();
        ctx.moveTo(-s * 0.55, dy + s * 0.28);
        ctx.lineTo(0, dy - s * 0.28);
        ctx.lineTo(s * 0.55, dy + s * 0.28);
        ctx.stroke();
      }
      break;

    case "shield": // max integrity — shield + plus
      ctx.beginPath();
      ctx.moveTo(0, -s * 0.85);
      ctx.lineTo(s * 0.7, -s * 0.45);
      ctx.lineTo(s * 0.7, s * 0.2);
      ctx.quadraticCurveTo(s * 0.55, s * 0.8, 0, s);
      ctx.quadraticCurveTo(-s * 0.55, s * 0.8, -s * 0.7, s * 0.2);
      ctx.lineTo(-s * 0.7, -s * 0.45);
      ctx.closePath();
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, -s * 0.3);
      ctx.lineTo(0, s * 0.35);
      ctx.moveTo(-s * 0.32, s * 0.02);
      ctx.lineTo(s * 0.32, s * 0.02);
      ctx.stroke();
      break;

    case "magnet": // pickup radius — horseshoe magnet
      ctx.beginPath();
      ctx.arc(0, -s * 0.05, s * 0.55, Math.PI, 0);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(-s * 0.55, -s * 0.05);
      ctx.lineTo(-s * 0.55, s * 0.6);
      ctx.moveTo(s * 0.55, -s * 0.05);
      ctx.lineTo(s * 0.55, s * 0.6);
      ctx.stroke();
      ctx.fillRect(-s * 0.78, s * 0.5, s * 0.46, size * 0.14);
      ctx.fillRect(s * 0.32, s * 0.5, s * 0.46, size * 0.14);
      break;

    case "cross": // regen — medical cross
      ctx.lineWidth = size * 0.24;
      ctx.beginPath();
      ctx.moveTo(0, -s * 0.7);
      ctx.lineTo(0, s * 0.7);
      ctx.moveTo(-s * 0.7, 0);
      ctx.lineTo(s * 0.7, 0);
      ctx.stroke();
      break;

    case "burst": // knockback — radial burst
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * TAU;
        ctx.beginPath();
        ctx.moveTo(Math.cos(a) * s * 0.3, Math.sin(a) * s * 0.3);
        ctx.lineTo(Math.cos(a) * s * 0.85, Math.sin(a) * s * 0.85);
        ctx.stroke();
      }
      break;

    case "mesh": // node self-repair — grid
      for (let i = -1; i <= 1; i++) {
        ctx.beginPath();
        ctx.moveTo(i * s * 0.55, -s * 0.75);
        ctx.lineTo(i * s * 0.55, s * 0.75);
        ctx.moveTo(-s * 0.75, i * s * 0.55);
        ctx.lineTo(s * 0.75, i * s * 0.55);
        ctx.stroke();
      }
      break;

    default: // fallback dot
      ctx.beginPath();
      ctx.arc(0, 0, s * 0.5, 0, TAU);
      ctx.fill();
      break;
  }
  ctx.restore();
}
